import { compareDesc, formatDistanceToNow } from 'date-fns';
import { Button } from 'app/agriculture/components/Button/Button';
import { RichTextDisplay } from 'app/agriculture/components/RichTextDisplay/RichTextDisplay';
import { ProfileAvatar } from 'app/agriculture/infobox/common/ProfileAvatar';
import { CalendarIcon } from 'app/agriculture/icons';
import { primaryColourMain } from 'app/agriculture/material';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import './NotesForEntity.scss';
import { useNotesForEntity } from 'app/agriculture/infobox/notes/useNotesForEntity';
import { useUsers } from 'app/agriculture/hooks/users/useUsers';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { TextWithIcon } from 'app/agriculture/components/TextWithIcon/TextWithIcon';

interface NotesForEntityProps {
    entityId: string;
}

export const NotesForEntity = ({ entityId }: NotesForEntityProps) => {
    const routeActions = useRouteActions();
    const users = useUsers();

    const notes = useNotesForEntity(entityId);

    if (notes.length === 0) {
        return null;
    }

    const [latestNote] = notes.sort((a, b) =>
        // sort from most recent to least recent
        compareDesc(a.createdAt, b.createdAt),
    );

    if (latestNote == null) return null;

    const creator = users.find((user) => user.id === latestNote.creatorId);
    const creatorName = creator?.name ?? 'Unknown';

    return (
        <ContentBox.Section disableDivider>
            <div className="latest-note-container">
                <label className="stat-display-name">Latest Note</label>
                <div className={'latest-note-header'}>
                    <TextWithIcon
                        id="note-user"
                        className="latest-note-user"
                        Icon={() => <ProfileAvatar name={creatorName} />}
                        tooltip={creatorName}
                    >
                        {creatorName}
                    </TextWithIcon>
                    <TextWithIcon
                        className="latest-note-timestamp"
                        Icon={() => <CalendarIcon size={18} color={primaryColourMain} />}
                        tooltip={latestNote.createdAt.toLocaleString()}
                    >
                        {formatDistanceToNow(latestNote.createdAt, {
                            addSuffix: true,
                        })}
                    </TextWithIcon>
                </div>
                <div className="latest-note-text">
                    <RichTextDisplay id="note-text" text={latestNote.text} />
                </div>
                {notes.length > 1 && (
                    <Button
                        id="show-all-notes-button"
                        variant="compact"
                        onClick={() => {
                            routeActions.openList('Note');
                        }}
                        endIcon="goto"
                        text="View All Notes"
                    />
                )}
            </div>
        </ContentBox.Section>
    );
};
